import {
  CLIENT_COMPLETED_REVIEW,
  OnboardingFundingCoverage,
  OnboardingFundingSources,
} from '@client';
import { SessionType, newUuid } from '@shared';
import { Widget } from '@typeform/embed-react';
import { FileDown } from 'lucide-react';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { OnboardingChrome, PageLink, getLastSurveyId } from '../components';
import { SlideContent } from '../components/SlideContent';
import { Button } from '../components/ui/button';
import {
  putClientOnboardingSlideProgressByClientIdRequest,
  selectClient,
  selectClientOnboardingSlideProgress,
  selectSession,
} from '../model';
import { navigateToPrintReport } from '../util/printUtils';
import styles from './OnboardingDownloadReport.module.css';

interface FeedbackSurveyProps {
  client: Client;
}

const FeedbackSurvey = ({ client }: FeedbackSurveyProps) => {
  const { clientEmail, clientFirstName, clientLastName, advisorId, surveys } = client;
  const surveyId = getLastSurveyId(surveys);
  const formId = 'm4H0GpSY';
  return (
    <div data-testid="feedback-survey">
      <Widget
        id={formId}
        className={styles.surveyContainer}
        hidden={{
          consumer_email: clientEmail,
          consumer_first_name: clientFirstName,
          advisor_id: advisorId,
          survey_id: surveyId ?? 'xxxxx',
          organization_name: 'xxxxx',
          consumer_last_name: clientLastName,
        }}
      />
    </div>
  );
};

export function OnboardingDownloadReport({ clientId }: ClientIdProps) {
  const client = useSelector(selectClient)!;
  const { sessionType } = useSelector(selectSession);
  const dispatch = useDispatch();
  const onboardingSlideProgress = useSelector(selectClientOnboardingSlideProgress);

  const handleDownloadPDF = () => {
    navigateToPrintReport(clientId);
  };

  useEffect(() => {
    if (
      (onboardingSlideProgress && onboardingSlideProgress.hasClientCompletedOnboarding) ||
      sessionType !== SessionType.client
    ) {
      return;
    }
    const clientOnboardingSlideProgressId =
      onboardingSlideProgress?.clientOnboardingSlideProgressId ?? newUuid();
    const updatedClientTags = [...client.clientTags, CLIENT_COMPLETED_REVIEW];
    dispatch(
      putClientOnboardingSlideProgressByClientIdRequest({
        clientOnboardingSlideProgressId,
        clientId,
        hasClientCompletedOnboarding: true,
        clientTags: updatedClientTags,
      }),
    );
  }, [clientId, dispatch]);

  return (
    <OnboardingChrome
      slidePage={OnboardingDownloadReport}
      nextButtonLabel="How did we do?"
      previousSlidePage={OnboardingFundingCoverage}
    >
      <SlideContent
        headerText="Download your report and share how we did"
        data-testid="download-report-header"
      >
        <div className="mt-8 flex w-full flex-col gap-6 md:flex-row md:gap-8 lg:gap-10">
          <div className="hidden md:flex md:flex-shrink-0">
            <FeedbackSurvey client={client} />
          </div>
          <div className="flex flex-col items-center md:ml-auto md:w-56 lg:w-64">
            <div className="flex w-full flex-col gap-4">
              <button
                onClick={handleDownloadPDF}
                className="bg-primary hover:bg-primary/90 mt-8 flex w-full cursor-pointer items-center justify-center rounded-md px-4 py-2 text-xl text-white md:mt-0 md:text-base"
                data-testid="download-report-button"
              >
                Download Report
                <FileDown className="ml-2" />
              </button>
              <PageLink
                to={OnboardingFundingSources}
                targetProps={{ clientId }}
              >
                <Button
                  variant="outline"
                  className="w-full rounded-md bg-white text-gray-800 hover:bg-gray-100"
                >
                  Update Financing Plan
                </Button>
              </PageLink>
            </div>
          </div>
        </div>
      </SlideContent>
    </OnboardingChrome>
  );
}
